import { parseNumberString } from "components/common/PrettyNumberTextField.support";
import {
  FILTER_KIND_OUTPUT,
  GROUPING_COLUMN_PREFIX,
  GROUPING_COLUMN_SUFFIX,
  GROUP_ROW_GROUP,
  ISO_DATE_PATTERN,
  RANGE_GROUPING_LABEL_MAX_FRACTION_DIGITS,
  RANGE_GROUPING_MAX_GROUPS,
  RANGE_GROUPING_ORDINAL_PREFIX_PATTERN,
  RANGE_GROUPING_TWO_DIGIT_LIMIT,
  RANGE_NULL_TARGET_FIRST,
  RANGE_NULL_TARGET_LAST,
  RANGE_NULL_TARGET_NO_VALUE,
  RANGE_ROW_ERROR_NOT_A_DATE,
  RANGE_ROW_ERROR_NOT_A_NUMBER,
  RANGE_ROW_ERROR_NOT_ASCENDING,
  RANGE_VALUE_TYPE_DATE,
  RANGE_VALUE_TYPE_NUMBER,
  RANGE_VALUE_TYPE_STRING,
  STRING_COLUMN_TYPE,
} from "components/reportsV3/types/constants";
import { formatDateValue } from "components/reportsV3/utils/dateUtils";
import {
  filterValueMultiplier,
  scaleFilterValue,
  subTypeValueAffixes,
  unscaleFilterValue,
} from "components/reportsV3/utils/numberUtils";
import {
  buildMatchWhen,
  fillMatchTemplate,
  isBlankMatchText,
  isMatchWhen,
  matchWhenText,
} from "components/reportsV3/utils/stringMatchUtils";

/** Returns the grouping column name for a source column. */
export function buildGroupingColumnName(sourceColumn) {
  return `${GROUPING_COLUMN_PREFIX}${sourceColumn}${GROUPING_COLUMN_SUFFIX}`;
}

/** Returns the source column of a grouping column name, or null. */
export function groupingColumnSource(name) {
  if (typeof name !== "string") return null;
  const minLength =
    GROUPING_COLUMN_PREFIX.length + GROUPING_COLUMN_SUFFIX.length;
  if (
    name.length <= minLength ||
    !name.startsWith(GROUPING_COLUMN_PREFIX) ||
    !name.endsWith(GROUPING_COLUMN_SUFFIX)
  ) {
    return null;
  }
  return name.slice(
    GROUPING_COLUMN_PREFIX.length,
    name.length - GROUPING_COLUMN_SUFFIX.length
  );
}

/** Returns whether a column name is a grouping column name. */
export function isGroupingColumn(name) {
  return groupingColumnSource(name) !== null;
}

/** Removes filter wrappers naming a grouping column that has no range grouping. */
export function withoutOrphanedGroupingFilters(filters, rangeGroupings) {
  const isOrphan = (column) => {
    const source = groupingColumnSource(column);
    return source !== null && !(source in (rangeGroupings ?? {}));
  };
  return (filters ?? []).filter((wrapper) => {
    const column = wrapper?._spec?.column;
    const columns = Array.isArray(column) ? column : [column];
    return !columns.some(isOrphan);
  });
}

/** Returns the ordinal width for a number of range groups. */
export function rangeOrdinalWidth(rangeGroupCount) {
  return rangeGroupCount <= RANGE_GROUPING_TWO_DIGIT_LIMIT ? 2 : 3;
}

/** Formats a threshold for display. */
export function formatThreshold(value) {
  return value.toLocaleString(undefined, {
    maximumFractionDigits: RANGE_GROUPING_LABEL_MAX_FRACTION_DIGITS,
  });
}

/** Returns a threshold formatter that adds a column sub-type's prefix and suffix. */
export function subTypeThresholdFormatter(subType) {
  const { prefix, suffix } = subTypeValueAffixes(subType);
  return (value) => `${prefix}${formatThreshold(value)}${suffix}`;
}

/** Returns a stored threshold as the range dialog shows it: a formatted date, or a number scaled and marked for the column sub-type. */
export function formatStoredThreshold(value, subType, dateFormat) {
  if (typeof value === "string") return formatDateValue(value, dateFormat);
  return subTypeThresholdFormatter(subType)(
    scaleFilterValue(value, filterValueMultiplier(subType))
  );
}

/** Returns a heading line for a range-grouped column then one line per range and the otherwise line, or null when the column has no range grouping. */
export function describeRangeGroupedColumn(
  column,
  rangeGroupings,
  { displayName, formatValue, templates }
) {
  const source = groupingColumnSource(column);
  const grouping = source === null ? null : rangeGroupings?.[source];
  if (!grouping?.expression) return null;
  const shownName = displayName(column);
  const name =
    shownName === column ? grouping.displayName ?? shownName : shownName;
  const fill = (template, value, label) =>
    template.replace("{value}", value).replace("{label}", label);
  const { expression } = grouping;
  const rangeLines = (expression.cases || [])
    .filter((c) => c?.when?.func === "LT" || isMatchWhen(c?.when))
    .map((c) =>
      c.when.func === "LT"
        ? fill(templates.lessThan, formatValue(source, c.when.b), c.then)
        : fill(templates.matches, matchWhenText(c.when), c.then)
    );
  const otherwiseLines =
    expression.otherwise === undefined || expression.otherwise === null
      ? []
      : [templates.otherwise.replace("{label}", expression.otherwise)];
  return [
    templates.heading.replace("{name}", name),
    ...rangeLines,
    ...otherwiseLines,
  ];
}

/** Returns the range value type for a column type. */
export function rangeValueType(columnType) {
  if (columnType === STRING_COLUMN_TYPE) return RANGE_VALUE_TYPE_STRING;
  return columnType === "DATE"
    ? RANGE_VALUE_TYPE_DATE
    : RANGE_VALUE_TYPE_NUMBER;
}

function fillTemplate(template, from, to, formatValue) {
  return template
    .replace("{from}", from === undefined ? "" : formatValue(from))
    .replace("{to}", to === undefined ? "" : formatValue(to));
}

/** Returns one default label per row from per-row values (null for none). */
export function defaultRowLabels(values, templates, formatValue) {
  let from;
  return values.map((value, i) => {
    const to = value === null || value === undefined ? undefined : value;
    let label;
    if (i === values.length - 1) {
      label =
        from === undefined
          ? ""
          : fillTemplate(templates.andAbove, from, undefined, formatValue);
    } else if (to === undefined) {
      label = "";
    } else if (from === undefined) {
      label = fillTemplate(templates.lessThan, undefined, to, formatValue);
    } else {
      label = fillTemplate(templates.fromLessThan, from, to, formatValue);
    }
    if (to !== undefined) from = to;
    return label;
  });
}

/** Returns one default label per match row from per-row values (null for none). */
export function defaultMatchRowLabels(values, templates) {
  const last = values.length - 1;
  return values.map((value, i) => {
    if (i === last) {
      return values.slice(0, last).some((v) => v !== null)
        ? templates.theRest
        : "";
    }
    return value === null ? "" : fillMatchTemplate(templates.matches, value);
  });
}

/** Returns a new no-value entry for the No Value target with the default label. */
export function defaultNoValueEntry() {
  return { nullTarget: RANGE_NULL_TARGET_NO_VALUE, labelOverride: null };
}

/** Returns null for missing or blank text, else the text unchanged. */
export function normaliseLabelOverride(text) {
  return (text ?? "").trim() === "" ? null : text;
}

/** Returns the ordered range labels and the no-value label. */
export function buildRangeLabels(
  thresholds,
  templates,
  formatValue = formatThreshold,
  labelOverrides = [],
  noValueLabelOverride = null
) {
  const descriptions = defaultRowLabels(
    [...thresholds, null],
    templates,
    formatValue
  );
  return ordinalLabels(
    descriptions,
    labelOverrides,
    templates.noValue,
    noValueLabelOverride
  );
}

function ordinalLabels(
  descriptions,
  labelOverrides,
  noValueText,
  noValueLabelOverride
) {
  const width = rangeOrdinalWidth(descriptions.length);
  const rangeLabels = descriptions.map((description, i) => {
    const override = labelOverrides[i];
    if (override !== null && override !== undefined) return override;
    return `${String(i + 1).padStart(width, "0")}: ${description}`;
  });
  const noValueLabel =
    noValueLabelOverride !== null && noValueLabelOverride !== undefined
      ? noValueLabelOverride
      : `${"9".repeat(width)}: ${noValueText}`;
  return { rangeLabels, noValueLabel };
}

/** Returns the ordered match labels and the no-value label. */
export function buildMatchLabels(
  patterns,
  templates,
  labelOverrides = [],
  noValueLabelOverride = null
) {
  return ordinalLabels(
    defaultMatchRowLabels([...patterns, null], templates),
    labelOverrides,
    templates.noValue,
    noValueLabelOverride
  );
}

/** Returns the label rows with no value are grouped under for a null target. */
export function nullCaseLabel(rangeLabels, noValueLabel, nullTarget) {
  if (nullTarget === RANGE_NULL_TARGET_FIRST) return rangeLabels[0];
  if (nullTarget === RANGE_NULL_TARGET_LAST)
    return rangeLabels[rangeLabels.length - 1];
  return noValueLabel;
}

/** Returns the CASE expression grouping a source column by thresholds, labelled as shown and compared divided by multiplier. */
export function buildGroupingExpression(
  sourceColumn,
  thresholds,
  templates,
  formatValue = formatThreshold,
  labelOverrides = [],
  nullTarget = RANGE_NULL_TARGET_NO_VALUE,
  noValueLabelOverride = null,
  multiplier = 1
) {
  if (!thresholds.length) throw new Error("No thresholds");
  const { rangeLabels, noValueLabel } = buildRangeLabels(
    thresholds,
    templates,
    formatValue,
    labelOverrides,
    noValueLabelOverride
  );
  const source = () => ({ func: "VAR", name: sourceColumn });
  return {
    func: "CASE",
    cases: [
      {
        when: { func: "IS_NULL", value: source() },
        then: nullCaseLabel(rangeLabels, noValueLabel, nullTarget),
      },
      ...thresholds.map((threshold, i) => ({
        when: {
          func: "LT",
          a: source(),
          b: unscaleFilterValue(threshold, multiplier),
        },
        then: rangeLabels[i],
      })),
    ],
    otherwise: rangeLabels[rangeLabels.length - 1],
  };
}

/** Returns the aggregate entry for a range grouping. */
export function buildGroupingAggregateEntry({
  sourceColumn,
  displayName,
  thresholds,
  templates,
  formatValue = formatThreshold,
  labelOverrides = [],
  nullTarget = RANGE_NULL_TARGET_NO_VALUE,
  noValueLabelOverride = null,
  multiplier = 1,
}) {
  return {
    column: buildGroupingColumnName(sourceColumn),
    displayName,
    expression: buildGroupingExpression(
      sourceColumn,
      thresholds,
      templates,
      formatValue,
      labelOverrides,
      nullTarget,
      noValueLabelOverride,
      multiplier
    ),
  };
}

/** Returns the CASE expression grouping a source column by match patterns. */
export function buildMatchGroupingExpression(
  sourceColumn,
  patterns,
  templates,
  labelOverrides = [],
  nullTargetIndex = null,
  noValueLabelOverride = null
) {
  if (!patterns.length) throw new Error("No patterns");
  const { rangeLabels, noValueLabel } = buildMatchLabels(
    patterns,
    templates,
    labelOverrides,
    noValueLabelOverride
  );
  const validTarget =
    Number.isInteger(nullTargetIndex) &&
    nullTargetIndex >= 0 &&
    nullTargetIndex < rangeLabels.length;
  return {
    func: "CASE",
    cases: [
      {
        when: { func: "IS_NULL", value: { func: "VAR", name: sourceColumn } },
        then: validTarget ? rangeLabels[nullTargetIndex] : noValueLabel,
      },
      ...patterns.map((pattern, i) => ({
        when: buildMatchWhen(sourceColumn, pattern),
        then: rangeLabels[i],
      })),
    ],
    otherwise: rangeLabels[rangeLabels.length - 1],
  };
}

/** Returns the aggregate entry for a string range grouping. */
export function buildMatchGroupingAggregateEntry({
  sourceColumn,
  displayName,
  patterns,
  templates,
  labelOverrides = [],
  nullTargetIndex = null,
  noValueLabelOverride = null,
}) {
  return {
    column: buildGroupingColumnName(sourceColumn),
    displayName,
    expression: buildMatchGroupingExpression(
      sourceColumn,
      patterns,
      templates,
      labelOverrides,
      nullTargetIndex,
      noValueLabelOverride
    ),
  };
}

/** Returns whether a label starts with an ordinal prefix. */
export function hasOrdinalPrefix(label) {
  return (
    typeof label === "string" &&
    RANGE_GROUPING_ORDINAL_PREFIX_PATTERN.test(label)
  );
}

function labelOverrideOf(label) {
  return hasOrdinalPrefix(label) ? null : label;
}

/** Returns the value, multiplied by multiplier, and label override of each range of a grouping expression. */
export function parseRangeRows(expression, multiplier = 1) {
  const rangeCases = (expression?.cases || []).filter(
    (c) => c?.when?.func === "LT" || isMatchWhen(c?.when)
  );
  const hasOtherwise =
    expression?.otherwise !== undefined && expression?.otherwise !== null;
  if (!rangeCases.length && !hasOtherwise) return [];
  return [
    ...rangeCases.map((c) => ({
      value:
        c.when.func === "LT"
          ? scaleFilterValue(c.when.b, multiplier)
          : matchWhenText(c.when),
      labelOverride: labelOverrideOf(c.then),
    })),
    { value: null, labelOverride: labelOverrideOf(expression.otherwise) },
  ];
}

/** Returns the no-value entry of a grouping expression. */
export function parseNoValueEntry(
  expression,
  valueType = RANGE_VALUE_TYPE_NUMBER
) {
  const cases = expression?.cases || [];
  const nullCase = cases.find((c) => c?.when?.func === "IS_NULL");
  if (!nullCase) return defaultNoValueEntry();
  if (valueType === RANGE_VALUE_TYPE_STRING) {
    const targets = [
      ...cases.filter((c) => isMatchWhen(c?.when)).map((c) => c.then),
      expression.otherwise,
    ];
    const index = targets.indexOf(nullCase.then);
    if (index >= 0) return { nullTarget: index, labelOverride: null };
    return {
      nullTarget: RANGE_NULL_TARGET_NO_VALUE,
      labelOverride: labelOverrideOf(nullCase.then ?? null),
    };
  }
  const firstLtCase = cases.find((c) => c?.when?.func === "LT");
  const { then } = nullCase;
  if (firstLtCase && then === firstLtCase.then) {
    return { nullTarget: RANGE_NULL_TARGET_FIRST, labelOverride: null };
  }
  if (then === expression.otherwise) {
    return { nullTarget: RANGE_NULL_TARGET_LAST, labelOverride: null };
  }
  return {
    nullTarget: RANGE_NULL_TARGET_NO_VALUE,
    labelOverride: hasOrdinalPrefix(then) ? null : then ?? null,
  };
}

/** Returns one flag per label marking case- and white-space-insensitive duplicates. */
export function duplicateLabelFlags(labels) {
  const normalised = labels.map((label) =>
    String(label ?? "")
      .trim()
      .toUpperCase()
  );
  const counts = new Map();
  normalised.forEach((label) =>
    counts.set(label, (counts.get(label) || 0) + 1)
  );
  return normalised.map((label) => label !== "" && counts.get(label) > 1);
}

/** Returns range groupings keyed by source for grouping entries of an aggregate list. */
export function rangeGroupingsFromAggregate(aggregate) {
  const result = {};
  (aggregate || []).forEach((entry) => {
    const source = groupingColumnSource(entry?.column);
    if (source === null) return;
    const { column, displayName, expression } = entry;
    result[source] = { column, displayName, expression };
  });
  return result;
}

/** Returns an order with grouping column entries moved to their source columns. */
export function mapGroupingOrderToSource(order) {
  return (order || []).map((entry) => {
    const source = groupingColumnSource(entry?.column);
    return source === null ? entry : { ...entry, column: source };
  });
}

/** Returns the range groupings that are grouped and whose source is available. */
export function getActiveRangeGroupings(
  rangeGroupings,
  groupByColumns,
  availableColumns
) {
  const grouped = new Set(groupByColumns || []);
  const available = new Set(availableColumns || []);
  const result = {};
  Object.entries(rangeGroupings || {}).forEach(([source, entry]) => {
    if (grouped.has(buildGroupingColumnName(source)) && available.has(source)) {
      result[source] = entry;
    }
  });
  return result;
}

/** Returns an order with entries on active sources moved to their grouping columns. */
export function rewriteOrderForRangeGroupings(order, activeRangeGroupings) {
  const active = activeRangeGroupings || {};
  return (order || []).map((entry) =>
    Object.prototype.hasOwnProperty.call(active, entry?.column)
      ? { ...entry, column: buildGroupingColumnName(entry.column) }
      : entry
  );
}

/** Returns spec parts with active range groupings applied and inactive ones removed. */
export function applyRangeGroupingsToSpecParts({
  aggregate,
  outputColumns,
  order,
  filters,
  groupColumns,
  allValuesEnumerations,
  activeRangeGroupings,
}) {
  const activeEntries = Object.entries(activeRangeGroupings || {});
  const activeNames = new Set(
    activeEntries.map(([source]) => buildGroupingColumnName(source))
  );
  const isInactive = (name) => isGroupingColumn(name) && !activeNames.has(name);
  const withoutInactive = (names) =>
    new Set([...(names || [])].filter((name) => !isInactive(name)));

  const outputs = (outputColumns || []).filter(
    (col) => !isGroupingColumn(col.column)
  );
  activeEntries.forEach(([source, entry]) => {
    const sourceIndex = outputs.findIndex((col) => col.column === source);
    const groupingOutput = {
      column: buildGroupingColumnName(source),
      displayName: entry.displayName,
      notForExport: true,
    };
    outputs.splice(
      sourceIndex >= 0 ? sourceIndex : outputs.length,
      0,
      groupingOutput
    );
  });

  return {
    aggregate: [
      ...(aggregate || []).filter((entry) => !isGroupingColumn(entry?.column)),
      ...activeEntries.map(([, entry]) => entry),
    ],
    outputColumns: outputs,
    order: rewriteOrderForRangeGroupings(order, activeRangeGroupings).filter(
      (entry) => !isInactive(entry?.column)
    ),
    filters: (filters || []).filter((filter) => {
      const columns = Array.isArray(filter?.column)
        ? filter.column
        : [filter?.column];
      return !columns.some(isInactive);
    }),
    groupColumns: withoutInactive(groupColumns),
    allValuesEnumerations: withoutInactive(allValuesEnumerations),
  };
}

/** Returns whether a row shows the grouping value: no control column, no control value, or a Group row. */
export function takesGroupingValue(row, controlColumn) {
  if (!controlColumn) return true;
  const control = row?.[controlColumn];
  return (
    control === null || control === undefined || control === GROUP_ROW_GROUP
  );
}

/** Returns a source column's cell value, using the grouping value where the row takes it. */
export function rangeGroupedCellValue(
  row,
  sourceColumn,
  controlColumn,
  sourceValueGetter = (value) => value
) {
  if (takesGroupingValue(row, controlColumn)) {
    return row[buildGroupingColumnName(sourceColumn)];
  }
  return sourceValueGetter(row[sourceColumn]);
}

/** Returns whether text is a real "YYYY-MM-DD" date. */
export function isIsoDateString(text) {
  if (!ISO_DATE_PATTERN.test(text)) return false;
  const date = new Date(`${text}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(text);
}

/** Parses a threshold field's text as an ISO date, or as a number the way PrettyNumberTextField does. */
export function parseThresholdInput(text, valueType = RANGE_VALUE_TYPE_NUMBER) {
  const cleaned = (text || "").trim();
  if (cleaned === "") return { empty: true, valid: true, value: null };
  if (valueType === RANGE_VALUE_TYPE_DATE) {
    return isIsoDateString(cleaned)
      ? { empty: false, valid: true, value: cleaned }
      : { empty: false, valid: false, value: null };
  }
  const value = parseNumberString(cleaned);
  if (typeof value === "number" && Number.isFinite(value)) {
    return { empty: false, valid: true, value };
  }
  return { empty: false, valid: false, value: null };
}

function emptyDialogRow(id) {
  return { id, text: "", labelOverride: null };
}

/** Returns the dialog rows for a list of initial ranges. */
export function createDialogRows(initialRanges, makeId) {
  if (!initialRanges || !initialRanges.length)
    return [emptyDialogRow(makeId())];
  const rows = initialRanges.map((entry) => ({
    id: makeId(),
    text:
      entry?.value === null || entry?.value === undefined
        ? ""
        : String(entry.value),
    labelOverride: entry?.labelOverride ?? null,
  }));
  if (rows[rows.length - 1].text !== "") rows.push(emptyDialogRow(makeId()));
  return rows;
}

/** Returns the per-row errors and parsed values of the dialog rows. */
export function parseDialogRowValues(
  rows,
  valueType = RANGE_VALUE_TYPE_NUMBER
) {
  if (valueType === RANGE_VALUE_TYPE_STRING) {
    return {
      errors: rows.map(() => null),
      values: rows.map((row) => (isBlankMatchText(row.text) ? null : row.text)),
    };
  }
  const invalidError =
    valueType === RANGE_VALUE_TYPE_DATE
      ? RANGE_ROW_ERROR_NOT_A_DATE
      : RANGE_ROW_ERROR_NOT_A_NUMBER;
  let previous = null;
  const values = [];
  const errors = rows.map((row) => {
    const parsed = parseThresholdInput(row.text, valueType);
    values.push(null);
    if (parsed.empty) return null;
    if (!parsed.valid) return invalidError;
    const notAscending = previous !== null && parsed.value <= previous;
    previous = parsed.value;
    if (notAscending) return RANGE_ROW_ERROR_NOT_ASCENDING;
    values[values.length - 1] = parsed.value;
    return null;
  });
  return { errors, values };
}

/** Returns the rows with one row's text replaced, appending an open row after a valid last value. */
export function updateDialogRow(rows, index, text, valueType, newId) {
  const updated = rows.map((row, i) => (i === index ? { ...row, text } : row));
  if (
    index === rows.length - 1 &&
    rows.length < RANGE_GROUPING_MAX_GROUPS &&
    parseDialogRowValues(updated, valueType).values[index] !== null
  ) {
    updated.push(emptyDialogRow(newId));
  }
  return updated;
}

/** Returns the null target a range row's button selects, or null for no button. */
export function rowNullTarget(
  index,
  rowCount,
  valueType = RANGE_VALUE_TYPE_NUMBER,
  rowId = null
) {
  if (valueType === RANGE_VALUE_TYPE_STRING) return rowId;
  if (index === 0) return RANGE_NULL_TARGET_FIRST;
  if (rowCount >= 2 && index === rowCount - 1) return RANGE_NULL_TARGET_LAST;
  return null;
}

/** Returns whether a range row's null-target button shows as active. */
export function isNullTargetRow(
  index,
  rowCount,
  nullTarget,
  valueType = RANGE_VALUE_TYPE_NUMBER,
  rowId = null
) {
  if (valueType === RANGE_VALUE_TYPE_STRING) return nullTarget === rowId;
  if (rowCount === 1 && index === 0) {
    return (
      nullTarget === RANGE_NULL_TARGET_FIRST ||
      nullTarget === RANGE_NULL_TARGET_LAST
    );
  }
  const target = rowNullTarget(index, rowCount);
  return target !== null && target === nullTarget;
}

/** Returns the index of the row a string null target names, or null. */
export function nullTargetRowIndex(rows, nullTarget) {
  const index = rows.findIndex((row) => row.id === nullTarget);
  return index >= 0 ? index : null;
}

/** Returns the row id for a parsed string null target index, or the No Value target. */
export function nullTargetFromRowIndex(rows, target) {
  return typeof target === "number" && rows[target]
    ? rows[target].id
    : RANGE_NULL_TARGET_NO_VALUE;
}

/** Returns the string null target after deleting a row. */
export function nullTargetAfterDelete(rows, index, nullTarget) {
  return rows[index]?.id === nullTarget
    ? RANGE_NULL_TARGET_NO_VALUE
    : nullTarget;
}

/** Returns the per-row errors, values and labels, the No Value label, the thresholds and whether the rows can be saved. */
export function validateDialogRows(
  rows,
  valueType = RANGE_VALUE_TYPE_NUMBER,
  templates,
  formatValue = formatThreshold,
  noValue = defaultNoValueEntry()
) {
  const { errors, values } = parseDialogRowValues(rows, valueType);
  const defaultLabels =
    valueType === RANGE_VALUE_TYPE_STRING
      ? defaultMatchRowLabels(values, templates)
      : defaultRowLabels(values, templates, formatValue);
  const labels = rows.map((row, i) =>
    row.labelOverride !== null && row.labelOverride !== undefined
      ? row.labelOverride
      : defaultLabels[i]
  );
  const noValueDefaultLabel = templates.noValue;
  const noValueLabel =
    noValue.labelOverride !== null && noValue.labelOverride !== undefined
      ? noValue.labelOverride
      : noValueDefaultLabel;
  let duplicates;
  let noValueDuplicate = false;
  if (noValue.nullTarget === RANGE_NULL_TARGET_NO_VALUE) {
    [noValueDuplicate, ...duplicates] = duplicateLabelFlags([
      noValueLabel,
      ...labels,
    ]);
  } else {
    duplicates = duplicateLabelFlags(labels);
  }
  const leading = values.slice(0, -1);
  const lastRow = rows[rows.length - 1];
  return {
    errors,
    values,
    thresholds: leading.filter((value) => value !== null),
    defaultLabels,
    labels,
    duplicates,
    canSave:
      rows.length >= 2 &&
      leading.every((value) => value !== null) &&
      (lastRow.text || "").trim() === "" &&
      !duplicates.some(Boolean) &&
      !noValueDuplicate,
    noValueDefaultLabel,
    noValueLabel,
    noValueDuplicate,
  };
}

/** Returns the rows with one row's label override set, or cleared for blank text. */
export function setRowLabelOverride(rows, index, text) {
  const labelOverride = normaliseLabelOverride(text);
  return rows.map((row, i) => (i === index ? { ...row, labelOverride } : row));
}

/** Returns the column whose all-values setting a header's enum toggle controls. */
export function allValuesEnumerationKey(field, rangeGroupedColumns) {
  return rangeGroupedColumns?.has(field)
    ? buildGroupingColumnName(field)
    : field;
}

/** Returns the column a filter of the given kind on a display column is written against. */
export function filterTargetColumn(column, kind, rangeGroupedColumns) {
  return kind === FILTER_KIND_OUTPUT && rangeGroupedColumns?.has(column)
    ? buildGroupingColumnName(column)
    : column;
}

/** Returns the display column for a filter target column. */
export function filterDisplayColumn(targetColumn) {
  return groupingColumnSource(targetColumn) ?? targetColumn;
}

/** Returns whether another dialog row can be inserted. */
export function canInsertDialogRow(rows) {
  return rows.length < RANGE_GROUPING_MAX_GROUPS;
}

/** Returns the dialog rows without one row; the last row is never deleted. */
export function deleteDialogRow(rows, index) {
  if (index === rows.length - 1) return rows;
  return rows.filter((_, i) => i !== index);
}

/** Returns the dialog rows with an empty row inserted below the given row. */
export function insertDialogRow(rows, index, newId) {
  if (index === rows.length - 1 || !canInsertDialogRow(rows)) return rows;
  return [
    ...rows.slice(0, index + 1),
    emptyDialogRow(newId),
    ...rows.slice(index + 1),
  ];
}
